import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  TextField,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { ClearAllRounded } from "@mui/icons-material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { withPather } from "react-pather";
import TagChip from "./TagChip";
import { Modal } from "../modals";
import { useFilteredTag } from "../../../hooks";

const TagsSelectModal = ({
  pather,
  open,
  setOpen,
  pickedTagCodes,
  title,
  confirm,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { getFilteredTag } = useFilteredTag();

  const [tags, setTags] = useState([]);
  const [filteredTags, setFilterTags] = useState([]);
  const [selectedTagCodes, setSelectedTagCodes] = useState(pickedTagCodes);

  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(true);

  const fetchTags = () => {
    setLoading(true);
    axios
      .get(pather.back.Tags.list)
      .then((response) => {
        const data = response.data;
        const _tags = data.tags;

        const convTags = _tags.filter((tag) =>
          selectedTagCodes.includes(tag.code)
        );
        const nonConvTags = _tags.filter(
          (tag) => !selectedTagCodes.includes(tag.code)
        );
        setTags([...convTags, ...nonConvTags]);
      })
      .catch((error) => {
        console.log(error);
        enqueueSnackbar("Some error occurred during tags fetching", {
          variant: "error",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const toggleTag = (tagCode) => {
    selectedTagCodes.includes(tagCode)
      ? setSelectedTagCodes((currentTagCodes) => [
          ...currentTagCodes.filter((_tag_code) => _tag_code !== tagCode),
        ])
      : setSelectedTagCodes((currentTagCodes) => [...currentTagCodes, tagCode]);
  };

  useEffect(() => {
    setFilterTags(
      tags.filter((tag) => tag.name.toLowerCase().includes(search))
    );
  }, [search]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={title}
      confirm={() => confirm(selectedTagCodes)}
      preButtonsComp={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography component="div" sx={{ fontSize: "16px" }}>
            Selected tags: {selectedTagCodes.length}
          </Typography>
          {selectedTagCodes.length > 0 && (
            <Box>
              <Tooltip title="Clear all" placement="bottom" arrow>
                <IconButton onClick={() => setSelectedTagCodes([])}>
                  <ClearAllRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      }
    >
      <TextField
        label="Search"
        variant="standard"
        type="search"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value.trim())}
      />
      <Paper
        elevation={1}
        sx={{
          maxHeight: "300px",
          overflow: "scroll",
          px: 2,
          py: 1,
          borderEndStartRadius: "8px",
          borderEndEndRadius: "8px",
        }}
      >
        {loading ? (
          <CircularProgress size={30} />
        ) : (
          <FormGroup>
            {search ? (
              <>
                {filteredTags.length > 0 ? (
                  filteredTags.map((tag) => (
                    <FormControlLabel
                      key={tag.code}
                      control={
                        <Checkbox
                          checked={selectedTagCodes.includes(tag.code)}
                          onChange={() => toggleTag(tag.code)}
                        />
                      }
                      label={getFilteredTag(tag, search)}
                    />
                  ))
                ) : (
                  <Typography component="div" sx={{ fontSize: "16px" }}>
                    No matches
                  </Typography>
                )}
              </>
            ) : (
              tags.map((tag) => (
                <FormControlLabel
                  key={tag.code}
                  control={
                    <Checkbox
                      checked={selectedTagCodes.includes(tag.code)}
                      onChange={() => toggleTag(tag.code)}
                    />
                  }
                  label={<TagChip label={tag.name} color={tag.color} />}
                />
              ))
            )}
          </FormGroup>
        )}
      </Paper>
    </Modal>
  );
};

export default withPather()(TagsSelectModal);